/**
 * @file Weapons that are not being carried by anybody (issue #15).
 *
 * One entity shape covers a weapon for its whole time outside a holder: it is
 * spawned airborne by a throw, and converted in place to a floor pickup when it
 * hits a wall or runs out of range. Nothing is rebuilt on the way — the entity
 * keeps carrying the exact {@link ../components/Weapon.js Weapon} instance its
 * holder had, so stats survive the round trip and enemies can equip it by
 * moving the component rather than cloning it.
 *
 * Once grounded it also gains a `Pickup`, which is what lets the existing
 * {@link ../systems/PickupSystem.js} bob it, spin it and hand it to the player
 * with no new collection code.
 */

import {
  Transform,
  Velocity,
  Collider,
  Renderable,
  Lifetime,
  Pickup,
  InteractionTarget,
  Weapon,
  ThrownWeapon
} from '../components/index.js';
import { Layer } from '../config/Layers.js';
import { DROPS } from '../config/Powerups.js';
import { WEAPONS } from '../config/Weapons.js';
import { PALETTE } from '../config/Palette.js';
import { Prefab } from '../rendering/Prefabs.js';

/**
 * Powerup definitions synthesised for weapons on the floor, cached per weapon
 * key so grounding one allocates nothing.
 * @type {Map<string, import('../config/Powerups.js').PowerupDefinition>}
 */
const FLOOR_DEFINITIONS = new Map();

/**
 * @param {import('../components/Weapon.js').Weapon} weapon Weapon on the floor.
 * @returns {import('../config/Powerups.js').PowerupDefinition | null} Definition
 *   the pickup system can apply, or null for a weapon with no archetype behind it.
 */
function floorDefinition(weapon) {
  if (!WEAPONS[weapon.key]) return null;

  let definition = FLOOR_DEFINITIONS.get(weapon.key);
  if (!definition) {
    definition = Object.freeze({
      name: weapon.name,
      kind: 'weapon',
      color: PALETTE.pickupWeapon,
      weapon: weapon.key,
      weight: 0
    });
    FLOOR_DEFINITIONS.set(weapon.key, definition);
  }
  return definition;
}

/**
 * @param {import('../components/Weapon.js').Weapon} weapon Weapon to render.
 * @returns {string} Prefab key, falling back to the default sidearm's look for
 *   a weapon built outside the archetype table.
 */
function prefabFor(weapon) {
  return Prefab.weapon(WEAPONS[weapon.key] ? weapon.key : 'pistol');
}

/**
 * Launches the weapon an entity was carrying. The caller is responsible for
 * removing the `Weapon` component from the thrower first — this factory takes
 * ownership of the instance.
 *
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @param {object} options Throw setup.
 * @param {import('three').Vector3} options.position Release point.
 * @param {import('three').Vector3} options.direction Normalised throw direction.
 * @param {import('../components/Weapon.js').Weapon} options.weapon Weapon being thrown.
 * @param {import('../ecs/Entity.js').Entity} options.owner Thrower.
 * @returns {import('../ecs/Entity.js').Entity}
 */
export function createThrownWeapon(ctx, { position, direction, weapon, owner }) {
  const { world, meshFactory, config } = ctx;
  const throwing = config.throwing;
  const entity = world.createEntity('thrown-weapon');

  const transform = entity.add(new Transform(position.x, throwing.height, position.z));
  transform.rotationY = Math.atan2(direction.x, direction.z);

  const velocity = entity.add(new Velocity(throwing.speed, 0, 0));
  velocity.linear.set(direction.x, 0, direction.z).multiplyScalar(throwing.speed);
  // A throw flies straight: the movement system must not steer or damp it.
  velocity.steered = false;

  // A weapon in flight is not being aimed by anyone.
  weapon.triggerHeld = false;
  weapon.cooldown = 0;
  entity.add(weapon);

  entity.add(
    new ThrownWeapon({ ownerId: owner?.id ?? 0, range: throwing.range, grounded: false })
  );
  entity.add(new Lifetime(throwing.lifetime));

  // Not a trigger and not a projectile, so the collision system pushes it out of
  // walls like any other body — which is exactly how the throw system learns it
  // hit one.
  entity.add(Collider.fromRadius(throwing.radius, Layer.THROWN_WEAPON, Layer.WALL));

  const prefabKey = prefabFor(weapon);
  entity.add(
    new Renderable(prefabKey, meshFactory.acquire(prefabKey), { yOffset: 0, faceVelocity: true })
  );

  return entity;
}

/**
 * Drops a weapon straight onto the floor, e.g. the one a stunned enemy lets go
 * of. The caller must have detached the `Weapon` component from its holder.
 *
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @param {number} x World X.
 * @param {number} z World Z.
 * @param {import('../components/Weapon.js').Weapon} weapon Weapon being dropped.
 * @returns {import('../ecs/Entity.js').Entity}
 */
export function dropWeapon(ctx, x, z, weapon) {
  const { world, meshFactory, config } = ctx;
  const entity = world.createEntity('weapon-drop');

  entity.add(new Transform(x, DROPS.hoverHeight, z));

  weapon.triggerHeld = false;
  weapon.cooldown = 0;
  entity.add(weapon);

  entity.add(new ThrownWeapon({ grounded: true }));
  entity.add(new Lifetime(config.throwing.lifetime));

  const prefabKey = prefabFor(weapon);
  entity.add(new Renderable(prefabKey, meshFactory.acquire(prefabKey), { yOffset: 0 }));

  attachFloorPickup(entity, weapon);
  return entity;
}

/**
 * Converts an airborne weapon into a floor pickup, in place.
 *
 * @param {import('../ecs/Entity.js').Entity} entity Thrown-weapon entity.
 * @returns {void}
 */
export function groundWeapon(entity) {
  const marker = entity.get(ThrownWeapon);
  if (!marker || marker.grounded) return;
  marker.grounded = true;

  const velocity = entity.get(Velocity);
  if (velocity) {
    velocity.linear.set(0, 0, 0);
    velocity.desired.set(0, 0, 0);
  }

  const transform = entity.get(Transform);
  transform.position.y = DROPS.hoverHeight;

  // On the floor it bobs and spins like any other drop, so facing must come
  // from the transform again rather than from a velocity that is now zero.
  const renderable = entity.get(Renderable);
  if (renderable) renderable.faceVelocity = false;

  attachFloorPickup(entity, entity.get(Weapon));
}

/**
 * Gives a floor weapon the trigger collider and `Pickup` the existing pickup
 * system needs to hand it to the player.
 *
 * @param {import('../ecs/Entity.js').Entity} entity Weapon entity, already grounded.
 * @param {import('../components/Weapon.js').Weapon} weapon Weapon it carries.
 * @returns {void}
 * @private
 */
function attachFloorPickup(entity, weapon) {
  entity.add(
    new Collider({
      halfX: DROPS.pickupRadius,
      halfZ: DROPS.pickupRadius,
      layer: Layer.PICKUP,
      mask: Layer.PLAYER,
      isTrigger: true
    })
  );

  const definition = weapon ? floorDefinition(weapon) : null;
  // No archetype behind it means the player cannot re-equip it, but enemies
  // still can: they take the component itself rather than rebuilding it.
  // Carry the Weapon instance so InteractionSystem can swap it back (issue #94),
  // and add InteractionTarget so it shows the E prompt instead of auto-vacuum.
  if (definition) {
    entity.add(new Pickup(weapon.key, definition, weapon));
    entity.add(new InteractionTarget(definition.name));
  }
}
